// Players tab: who is in each variant now (table) and how the test filled up
// over time (cumulative line per variant, with the same numbers in the table).
import React, { useMemo, useState } from 'react';
import { Line } from '@ant-design/charts';
import { Alert, Progress, Segmented, Space, Table, Typography } from 'antd';
import type { ExpV2Counts, ExpV2Detail, ExpV2Timeline } from '@/types/experimentsV2';
import { countShares, istTime, type CountShareRow } from './display';
import {
  VARIANT_COLORS,
  cumulativeSeries,
  formatCount,
  variantColor,
  variantOrder,
  type SeriesPoint,
} from './results';

interface Props {
  test: ExpV2Detail;
  counts: ExpV2Counts | null;
  countsError: string | null;
  timeline: ExpV2Timeline | null;
  timelineError: string | null;
}

export const PlayersPanel: React.FC<Props> = ({
  test,
  counts,
  countsError,
  timeline,
  timelineError,
}) => {
  const [field, setField] = useState<'assigned' | 'exposed'>('assigned');
  const order = useMemo(() => variantOrder(test), [test]);
  const series = useMemo<SeriesPoint[]>(
    () => (timeline ? cumulativeSeries(timeline, order, field) : []),
    [timeline, order, field]
  );
  const seriesVariants = [...new Set(series.map((p) => p.variant))];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {countsError && <Alert type="error" showIcon message={countsError} />}
      {counts && (
        <Table<CountShareRow>
          size="small"
          rowKey="variant"
          pagination={false}
          dataSource={countShares(test, counts)}
          columns={[
            {
              title: 'Variant',
              dataIndex: 'variant',
              render: (v: string) => (
                <Space size={6}>
                  <span
                    aria-hidden
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: 5,
                      background: variantColor(order, v),
                    }}
                  />
                  {v}
                </Space>
              ),
            },
            { title: 'Assigned', dataIndex: 'assigned', align: 'right', render: formatCount },
            { title: 'In it now', dataIndex: 'open', align: 'right', render: formatCount },
            { title: 'Exposed', dataIndex: 'exposed', align: 'right', render: formatCount },
            {
              title: 'Exposure rate',
              key: 'rate',
              align: 'right',
              render: (_: unknown, r: CountShareRow) =>
                r.assigned === 0 ? '—' : `${Math.round((r.exposed / r.assigned) * 1000) / 10}%`,
            },
            {
              title: 'Share of players (planned)',
              key: 'share',
              width: 260,
              render: (_: unknown, r: CountShareRow) => (
                <Space size={8} style={{ width: '100%' }}>
                  <Progress
                    percent={r.share}
                    strokeColor={variantColor(order, r.variant)}
                    showInfo={false}
                    size="small"
                    style={{ width: 120, margin: 0 }}
                  />
                  <Typography.Text>
                    {r.share}% ({r.planned}%)
                  </Typography.Text>
                </Space>
              ),
            },
          ]}
        />
      )}
      <Typography.Text type="secondary">
        The table counts players from{' '}
        {counts?.stablePhaseFrom
          ? `the full range opening (${istTime(counts.stablePhaseFrom)} IST)`
          : 'the start'}
        ; the chart below covers the whole test, the safety phase included. QA-forced players are
        left out of both.
      </Typography.Text>

      <Space wrap>
        <Typography.Text strong>Players over time (cumulative, by IST day)</Typography.Text>
        <Segmented
          value={field}
          onChange={(v) => setField(v as 'assigned' | 'exposed')}
          options={[
            { value: 'assigned', label: 'Assigned' },
            { value: 'exposed', label: 'Exposed' },
          ]}
        />
      </Space>
      {timelineError && <Alert type="error" showIcon message={timelineError} />}
      {series.length > 0 ? (
        <Line
          data={series}
          xField="day"
          yField="value"
          colorField="variant"
          height={260}
          animate={false}
          scale={{
            y: { domainMin: 0, nice: true },
            color: {
              domain: seriesVariants,
              range: seriesVariants.map((v) => variantColor(order, v) ?? VARIANT_COLORS[0]),
            },
          }}
          style={{ lineWidth: 2 }}
          point={{ sizeField: 4 }}
          legend={{ color: { position: 'top' } }}
          axis={{ y: { labelFormatter: (v: number) => formatCount(Number(v)) } }}
          tooltip={{
            title: 'day',
            items: [(d: SeriesPoint) => ({ name: d.variant, value: formatCount(d.value) })],
          }}
        />
      ) : (
        !timelineError && <Typography.Text type="secondary">No players yet.</Typography.Text>
      )}
    </Space>
  );
};
